import React, { useState, useEffect, useCallback, useRef } from "react";
import "./ImageList.css";

const ImageList = ({ data }) => {
  const [loadedImages, setLoadedImages] = useState({});
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const gridRef = useRef(null);

  // Reset loaded state when data changes
  useEffect(() => {
    setLoadedImages({});
  }, [data]);

  // After mount / data change, catch images already loaded from cache
  useEffect(() => {
    if (!gridRef.current) return;
    const imgs = gridRef.current.querySelectorAll("img[data-index]");
    const alreadyLoaded = {};
    imgs.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        alreadyLoaded[img.dataset.index] = true;
      }
    });
    if (Object.keys(alreadyLoaded).length > 0) {
      setLoadedImages((prev) => ({ ...prev, ...alreadyLoaded }));
    }
  }, [data]);

  const handleImageLoad = useCallback((index) => {
    setLoadedImages((prev) => (prev[index] ? prev : { ...prev, [index]: true }));
  }, []);

  // ── Lightbox ──
  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goNext = useCallback(() => {
    setLightboxIndex((i) => (i < data.length - 1 ? i + 1 : i));
  }, [data.length]);

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, goNext, goPrev]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  return (
    <>
      {/* Grid */}
      <div className="portfolio-grid" ref={gridRef}>
        {data.map((item, index) => (
          <div
            className="portfolio-grid-item"
            key={index}
            onClick={() => openLightbox(index)}
          >
            {!loadedImages[index] && <div className="skeleton" />}
            <img
              data-index={index}
              src={item.url}
              alt={item.title || `Project ${index + 1}`}
              loading="lazy"
              className={loadedImages[index] ? "loaded" : ""}
              onLoad={() => handleImageLoad(index)}
            />
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="portfolio-lightbox"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
        >
          <div className="portfolio-lightbox-content">
            {/* Close */}
            <button
              className="portfolio-lightbox-close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            {/* Prev */}
            <button
              className="portfolio-lightbox-prev"
              onClick={goPrev}
              disabled={lightboxIndex === 0}
              aria-label="Previous image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            {/* Image */}
            <img
              src={data[lightboxIndex].url}
              alt={data[lightboxIndex].title || `Project ${lightboxIndex + 1}`}
            />

            {/* Next */}
            <button
              className="portfolio-lightbox-next"
              onClick={goNext}
              disabled={lightboxIndex === data.length - 1}
              aria-label="Next image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            {/* Counter */}
            <span className="portfolio-lightbox-counter">
              {lightboxIndex + 1} / {data.length}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageList;
